import { PAGE_CONTENT_TYPES } from '@core/constants/contentTypes.constants';
import { isFixture, isFixtureListingPage } from '@core/types/typeGuards';
import { Page } from '@core/types/content/contentTypes.types';
import { Profile } from '@graphql/index';
import { CleanContentful } from '@stadionhq-v3/clean-contentful';
import { fullPathToArray } from '@utils/strings.utils';

/**
 * `extractSlugsFromContentfulResponse` function is used to generate slugs from a Contentful response data.
 * It checks the __typename property of the data and creates slugs accordingly.
 * Key names in the returned object must match the dynamic segments of the paths in the filesystem.
 *
 * @param {Object} data - The response object from Contentful.
 * @returns {Object | undefined} - Returns an object with article slug if __typename is ARTICLE_PAGE, or an empty object for all other types. Returns undefined if the input is not an object.
 *
 * @example
 *  const response = {
 *      __typename: "ArticlePage",
 *      slug: "my-article"
 *  };
 *  const slugs = extractSlugsFromContentfulResponse(response);
 *  console.log(slugs);  // Outputs: { article: "my-article" }
 *
 */
export function extractSlugsFromContentfulResponse<T extends Page>(
  data: T
): { [key: string]: string | string[] } {
  // Note : Key names in the returned object must match the dynamic segments of the paths in the filesystem.
  switch (data.__typename) {
    case PAGE_CONTENT_TYPES.ARTICLE_PAGE:
      return data?.slug ? { article: data?.slug } : {};
    case PAGE_CONTENT_TYPES.CONTENT_PAGE:
      // Using the slug as fallback will prevent errors 500
      return data?.fullPath
        ? { content: fullPathToArray(data?.fullPath) }
        : data?.slug
          ? { content: [data?.slug] }
          : {};
    case PAGE_CONTENT_TYPES.NEWS_LISTING_PAGE: {
      const defaultNewsListingPage =
        data?.linkedFrom?.siteSettings?.[0]?.defaultNewsLandingPage?.slug;
      const isDefaultNewsListing = defaultNewsListingPage === data?.slug;
      return data?.slug && !isDefaultNewsListing
        ? { newsListing: data?.slug }
        : {};
    }
    case PAGE_CONTENT_TYPES.TEAM_PAGE:
      return {
        team: data.slug!,
      };
    case PAGE_CONTENT_TYPES.PROFILE_PAGE:
      return {
        profile: data.slug!,
        team: (data as CleanContentful<Profile>).associatedTeams?.[0]!.slug!,
      };
    case PAGE_CONTENT_TYPES.FIXTURE_PAGE:
      return isFixture(data)
        ? {
            fixture: data.slug!,
            team: data.team!.slug!,
            season: data.season!.dataProviderId!,
          }
        : {};
    case PAGE_CONTENT_TYPES.FIXTURE_LISTING_PAGE:
      return isFixtureListingPage(data)
        ? {
            team: data.team!.slug!,
          }
        : {};
    default:
      return {};
  }
}
